import React from 'react';
import { Box, Typography, Button, Paper, IconButton, Tooltip, Divider, Chip } from '@mui/material';
import {
  Add as AddIcon,
  DragIndicator as DragIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';

import { PresetAction, ImpactAiPreset, BatchGeneratePayload, SetContextPayload } from '../../types';
import {
  createDefaultAction,
  createDefaultCallAction,
  getActionTypeLabel,
} from '../../utils/actionDefaults';

import ActionEditor from './ActionEditor';
import AddActionMenu from './AddActionMenu';

interface ActionsEditorProps {
  preset: Partial<ImpactAiPreset>;
  onChange: (actions: PresetAction[]) => void;
  availablePresets?: ImpactAiPreset[];
}

// Extract context keys from actions
const extractContextKeys = (actions: PresetAction[], currentIndex: number): string[] => {
  const keys = new Set<string>();

  // Only look at actions before the current one
  const previousActions = actions.slice(0, currentIndex);

  previousActions.forEach((action) => {
    switch (action.type) {
      case 'templateInput': {
        // Extract variable names from template
        const templateMatches = Array.from(
          action.data.template.matchAll(/\{\{([^,}]+)(?:,[^}]+)?\}\}/g)
        );
        templateMatches.forEach((match) => {
          keys.add(match[1].trim());
        });
        break;
      }

      case 'choice':
        if (action.data.choiceId) {
          keys.add(action.data.choiceId);
        }
        break;

      case 'multipleChoice':
        if (action.data.choiceId) {
          keys.add(action.data.choiceId);
        }
        break;

      case 'call':
        // For set-context method, extract the keys being set
        if (action.data.method === 'set-context' && 'context' in action.data.payload) {
          Object.keys((action.data.payload as SetContextPayload).context).forEach((key) =>
            keys.add(key)
          );
        }
        // For batch-generate with target: 'context'
        if (action.data.method === 'batch-generate' && 'batches' in action.data.payload) {
          (action.data.payload as BatchGeneratePayload).batches.forEach((batch) => {
            if (
              batch.type === 'generative' &&
              batch.target === 'context' &&
              batch.outputToFieldMap
            ) {
              batch.outputToFieldMap.forEach((mapping) => {
                if (mapping.target) {
                  keys.add(mapping.target);
                }
              });
            }
          });
        }
        break;

      default:
        break;
    }
  });

  return Array.from(keys);
};

const ActionsEditor: React.FC<ActionsEditorProps> = ({
  preset,
  onChange,
  availablePresets = [],
}) => {
  const actions = preset.actions || [];
  const [expandedActions, setExpandedActions] = React.useState<Set<number>>(new Set([0]));
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [draggedIndex, setDraggedIndex] = React.useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = React.useState<number | null>(null);

  const handleAddAction = (type: string, method?: string) => {
    const newAction =
      type === 'call' && method ? createDefaultCallAction(method) : createDefaultAction(type);
    onChange([...actions, newAction]);
    setExpandedActions(new Set([...expandedActions, actions.length]));
    setAnchorEl(null);
  };

  const handleUpdateAction = (index: number, action: PresetAction) => {
    const newActions = [...actions];
    newActions[index] = action;
    onChange(newActions);
  };

  const handleDeleteAction = (index: number) => {
    const newActions = actions.filter((_, i) => i !== index);
    onChange(newActions);

    // Update expanded actions
    const newExpanded = new Set<number>();
    expandedActions.forEach((i: number) => {
      if (i < index) {
        newExpanded.add(i);
      } else if (i > index) {
        newExpanded.add(i - 1);
      }
    });
    setExpandedActions(newExpanded);
  };

  const handleDuplicateAction = (index: number) => {
    const actionToDuplicate = actions[index];
    const newActions = [...actions];
    newActions.splice(index + 1, 0, { ...actionToDuplicate });
    onChange(newActions);
    setExpandedActions(new Set([...expandedActions, index + 1]));
  };

  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', '');
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();

    if (draggedIndex === null || draggedIndex === dropIndex) {
      return;
    }

    const newActions = [...actions];
    const [draggedAction] = newActions.splice(draggedIndex, 1);
    newActions.splice(dropIndex, 0, draggedAction);

    onChange(newActions);

    // Update expanded actions indices
    const newExpanded = new Set<number>();
    expandedActions.forEach((i: number) => {
      if (i === draggedIndex) {
        newExpanded.add(dropIndex);
      } else if (i < draggedIndex && i < dropIndex) {
        newExpanded.add(i);
      } else if (i < draggedIndex && i >= dropIndex) {
        newExpanded.add(i + 1);
      } else if (i > draggedIndex && i <= dropIndex) {
        newExpanded.add(i - 1);
      } else if (i > draggedIndex && i > dropIndex) {
        newExpanded.add(i);
      }
    });
    setExpandedActions(newExpanded);

    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const toggleExpanded = (index: number) => {
    const newExpanded = new Set(expandedActions);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedActions(newExpanded);
  };

  const getActionSummary = (action: PresetAction): string => {
    switch (action.type) {
      case 'message':
        return action.data.text.substring(0, 50) + (action.data.text.length > 50 ? '...' : '');

      case 'choice':
        return `${Object.keys(action.data.choices).length} options`;

      case 'multipleChoice':
        return `${action.data.choices.length} options`;

      case 'templateInput':
        return (
          action.data.template.substring(0, 50) + (action.data.template.length > 50 ? '...' : '')
        );

      case 'call':
        return action.data.method;

      case 'subPreset': {
        if (!action.data.presetId) {
          return 'No preset selected';
        }
        const referencedPreset = availablePresets.find((p) => p.id === action.data.presetId);
        const presetName = referencedPreset?.title || action.data.presetId;
        return `Reference: ${presetName}`;
      }

      case 'locationInput':
        return `Load location input into context at key: ${action.data.key}`;

      default:
        return 'Unknown action';
    }
  };

  return (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant="h6">Actions ({actions.length})</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={(e) => setAnchorEl(e.currentTarget)}
          size="small"
        >
          Add Action
        </Button>
      </Box>

      {actions.length === 0 ? (
        <Paper
          sx={{
            p: 4,
            textAlign: 'center',
            backgroundColor: 'grey.50',
          }}
        >
          <Typography color="text.secondary" gutterBottom>
            No actions defined yet
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Actions define the conversation flow of your preset
          </Typography>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={(e) => setAnchorEl(e.currentTarget)}
          >
            Add First Action
          </Button>
        </Paper>
      ) : (
        <Box>
          {actions.map((action, index) => (
            <Paper
              // actions have no id and dont feel like adding them now
              // eslint-disable-next-line react/no-array-index-key
              key={`action-${index}-${action.type}`}
              sx={{
                mb: 2,
                transition: 'all 0.2s ease',
                opacity: draggedIndex === index ? 0.5 : 1,
                backgroundColor:
                  dragOverIndex === index && draggedIndex !== index ? 'primary.50' : 'transparent',
                transform: draggedIndex === index ? 'rotate(2deg)' : 'none',
              }}
              elevation={draggedIndex === index ? 4 : 1}
              onDragOver={(e) => handleDragOver(e, index)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, index)}
            >
              <Box
                display="flex"
                alignItems="center"
                p={2}
                sx={{
                  backgroundColor: expandedActions.has(index) ? 'grey.50' : 'transparent',
                }}
              >
                <Box
                  mr={1}
                  draggable
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragEnd={handleDragEnd}
                  sx={{
                    cursor: 'grab',
                    padding: '4px',
                    borderRadius: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    '&:hover': {
                      backgroundColor: 'grey.100',
                    },
                    '&:active': {
                      cursor: 'grabbing',
                    },
                  }}
                >
                  <DragIcon color="action" />
                </Box>

                <Box flex={1} sx={{ cursor: 'pointer' }} onClick={() => toggleExpanded(index)}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Chip
                      label={`${index + 1}. ${getActionTypeLabel(action.type)}`}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                    <Typography variant="body2" color="text.secondary">
                      {getActionSummary(action)}
                    </Typography>
                  </Box>
                </Box>

                <Box display="flex" alignItems="center" gap={0.5}>
                  <Tooltip title="Duplicate action">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDuplicateAction(index);
                      }}
                    >
                      <CopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete action">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteAction(index);
                      }}
                      color="error"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <IconButton size="small" onClick={() => toggleExpanded(index)}>
                    {expandedActions.has(index) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                </Box>
              </Box>

              {expandedActions.has(index) && (
                <>
                  <Divider />
                  <Box p={3}>
                    <ActionEditor
                      action={action}
                      onChange={(updatedAction) => handleUpdateAction(index, updatedAction)}
                      contextKeys={[
                        ...Object.keys(preset.initialContext || {}),
                        ...extractContextKeys(actions, index),
                      ]}
                    />
                  </Box>
                </>
              )}
            </Paper>
          ))}
        </Box>
      )}

      <AddActionMenu
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        onAddAction={handleAddAction}
      />
    </Box>
  );
};

export default ActionsEditor;
